import { ReactElement, createContext, useState, useEffect, useRef, useCallback } from "react";
import { stateType } from "../../store";
import { connect } from "react-redux";
import { cartItem, extrasCartItem } from "../../state_management/reducers/cart";
import { orderInfo, orderType } from "../../state_management/reducers/order";
import { setOrderDetails } from "../../state_management/actions";
import { orderDetailsType } from "../../state_management/actionTypes";
import { useCouponParams } from "../../AppProvider";

export const ServiceDurationContext = createContext<
  [number, (data: number) => void]
>([0, () => {}]);

export const TimeSelectionWarningContext = createContext<
  [boolean, (show: boolean) => void]
>([false, () => {}]);

export interface ZeroSummaryItem {
  id: number;
  name: string;
  photo_path: string | null;
  price: number;
  price_with_discount: number;
  discount?: number | null;
  duration: number;
  quantity: number;
  type: "typical" | "extra" | "gift";
  parent_id?: number;
  rawItem: any;
}

export interface ZeroItemsContextType {
  zeroItemsMap: Record<string, ZeroSummaryItem>;
  setZeroItem: (key: string, item: ZeroSummaryItem) => void;
  removeZeroItem: (key: string) => void;
  itemOrder: string[];
  registerItemKeys: (keys: string[]) => void;
}

export const ZeroItemsContext = createContext<ZeroItemsContextType>({
  zeroItemsMap: {},
  setZeroItem: () => {},
  removeZeroItem: () => {},
  itemOrder: [],
  registerItemKeys: () => {}
});

const CheckOutProvider = ({
  children,
  cart,
  extras,
  orderInfo,
  orderDetails,
  setOrderDetails,
  noCouponCheck,
  directDetails,
  direct,
  pageCount
}: {
  children: ReactElement;
  cart: cartItem[];
  extras: extrasCartItem[];
  orderInfo: orderInfo;
  orderDetails: orderType;
  setOrderDetails: (data: Partial<orderType>) => void;
  noCouponCheck: boolean;
  directDetails: orderDetailsType | undefined;
  direct: boolean;
  pageCount: number;
}) => {
  const duration = (() => {
    const getDuration = (duration: number) => {
      const isNum = !isNaN(parseFloat(`${duration}`));

      return isNum ? duration : 0;
    };
    const durationArray = cart.map(
      (item) => getDuration(item.item.duration) * item.quantity
    );

    const extraDurationArray = extras.map(
      (item) => getDuration(item.item.duration) * item.quantity
    );

    const durationTotal = [...durationArray, ...extraDurationArray].reduce(
      (a, b) => a + b,
      0
    );
    return durationTotal;
  })();

  const [serviceDuration, setServiceDuration] = useState(duration);
  const [showTimeSelectionWarning, setShowTimeSelectionWarning] = useState(false);
  const [zeroItemsMap, setZeroItemsMap] = useState<Record<string, ZeroSummaryItem>>({});
  const [itemOrder, setItemOrder] = useState<string[]>([]);
  const prevPageCountRef = useRef(pageCount);

  useEffect(() => {
    if (pageCount > prevPageCountRef.current) {
      setZeroItemsMap({});
    }
    prevPageCountRef.current = pageCount;
  }, [pageCount]);

  const setZeroItem = useCallback((key: string, item: ZeroSummaryItem) => {
    setZeroItemsMap((prev) => ({ ...prev, [key]: item }));
  }, []);

  const removeZeroItem = useCallback((key: string) => {
    setZeroItemsMap((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  }, []);

  const registerItemKeys = useCallback((keys: string[]) => {
    setItemOrder((prev) => {
      const existing = new Set(prev);
      const toAppend = keys.filter((k) => !existing.has(k));
      return toAppend.length > 0 ? [...prev, ...toAppend] : prev;
    });
  }, []);

  useEffect(() => {
    if (pageCount !== 1) {
      setShowTimeSelectionWarning(false);
    }
  }, [pageCount]);

  useCouponParams({
    cart,
    extras,
    directData: directDetails?.data,
    direct,
    inactive: noCouponCheck,
    pageCount,
    orderDetails,
    setOrderDetails
  });

  return (
    <ServiceDurationContext.Provider
      value={[serviceDuration, setServiceDuration]}
    >
      <TimeSelectionWarningContext.Provider
        value={[showTimeSelectionWarning, setShowTimeSelectionWarning]}
      >
        <ZeroItemsContext.Provider
          value={{
            zeroItemsMap,
            setZeroItem,
            removeZeroItem,
            itemOrder,
            registerItemKeys
          }}
        >
          {children}
        </ZeroItemsContext.Provider>
      </TimeSelectionWarningContext.Provider>
    </ServiceDurationContext.Provider>
  );
};

const mapStateToProps = (state: stateType) => ({
  pageCount: state.commonReducer.pageCount,
  cart: state.cartReducer.cart,
  extras: state.cartReducer.extras,
  orderDetails: state.orderReducer.details,
  orderDirect: state.orderReducer.direct,
  orderInfo: state.orderReducer.orderInfo,
  orderSuccess: state.orderReducer.success,
  settings: state.settingsReducer,
  addressBlacklisted: state.commonReloadReducer.addressBlacklisted,
  timeError: state.commonReloadReducer.timeError
});

const mapDispatchToProps = (dispatch: any) => ({
  setOrderDetails: (details: Partial<orderType>) => {
    dispatch(setOrderDetails(details) as any);
  }
});

export default connect(mapStateToProps, mapDispatchToProps)(CheckOutProvider);
